import type { EChartsOption } from "echarts";
import { HEXACO_FACTORS, PAMI_COLORS } from "./constants";

/**
 * Construction de l'option ECharts du profil PAMI, isolée de tout React.
 *
 * Ce module est le SEUL endroit où vit la forme du graphe. Il a deux
 * consommateurs :
 *
 * 1. `PamiChart.tsx`, pour le rendu interactif dans le navigateur ;
 * 2. les rapports générés, dont l'option part en JSON vers le service externe
 *    `generate-echarts-image` (repo `alphaby/generate-echarts-image`), qui la
 *    rend en PNG.
 *
 * Une option construite à deux endroits finirait par diverger — le graphe du
 * rapport ne ressemblerait plus à celui vu à l'écran. D'où l'absence d'import
 * React ici : la contrainte est volontaire, elle garantit que le fichier reste
 * importable côté backend.
 */

/** Couleur de chaque domaine : les 6 facteurs HEXACO + la motivation. */
export const REGION_COLORS: Record<string, string> = {
  ...Object.fromEntries(
    HEXACO_FACTORS.map((f) => [`${f.label} (${f.key})`, f.color]),
  ),
  Motivation: PAMI_COLORS.motivation,
};

/** Les 20 traits, dans l'ordre d'affichage : 12 facettes HEXACO + 8 motivations. */
const RAW_TRAITS = [
  ...HEXACO_FACTORS.flatMap((f) =>
    f.facets.map((facet) => ({
      name: facet.label,
      key: facet.prefix,
      region: `${f.label} (${f.key})`,
    })),
  ),
  { name: "Autonomie", key: "Auto", region: "Motivation" },
  { name: "Équilibre vie pro/perso", key: "Equi", region: "Motivation" },
  { name: "Qualité management", key: "Manag", region: "Motivation" },
  { name: "Réalisation de soi", key: "Rea", region: "Motivation" },
  { name: "Revenus", key: "Rev", region: "Motivation" },
  { name: "Sécurité pro", key: "Secu", region: "Motivation" },
  { name: "Statut social", key: "Statut", region: "Motivation" },
  { name: "Variété des tâches", key: "Var", region: "Motivation" },
];

/** Une part du camembert : la valeur du trait, colorée par son domaine. */
export interface PamiSeriesItem {
  value: number;
  name: string;
  region: string;
  itemStyle: { color: string | undefined };
  /** Présent en mode sérialisable uniquement (cf. `buildPamiPolarOption`). */
  label?: { formatter: string };
}

const getSafeKey = (str: string) => str.replace(/[^a-zA-Z0-9]/g, "");

/** Coupe un nom trop long sur 2 lignes (équilibrées) au niveau d'un espace. */
function wrapName(name: string, maxLen = 16): string {
  if (name.length <= maxLen) return name;
  const words = name.split(" ");
  if (words.length < 2) return name;
  let line1 = "";
  let i = 0;
  for (; i < words.length; i++) {
    const next = (line1 + " " + words[i]).trim();
    if (line1 && next.length > maxLen) break;
    line1 = next;
  }
  const line2 = words.slice(i).join(" ");
  return line2 ? `${line1}\n${line2}` : line1;
}

/**
 * Styles des étiquettes, à l'échelle `k`. Le rapport imprimé passe 2 : la roue
 * y est rendue sur 1200 px pour 150 mm, et les 12-13 px du portail tombaient
 * à moins de 5 pt sur le papier.
 */
function buildRichTextStyles(k = 1) {
  const styles: Record<string, unknown> = {};
  Object.entries(REGION_COLORS).forEach(([region, color]) => {
    const key = getSafeKey(region);
    styles[`${key}Badge`] = {
      backgroundColor: color,
      color: "#fff",
      borderRadius: 15 * k,
      padding: [4 * k, 8 * k],
      fontWeight: "bold",
      fontSize: 12 * k,
      fontFamily: "sans-serif",
      shadowColor: "rgba(0,0,0,0.2)",
      shadowBlur: 3 * k,
      shadowOffsetY: 1 * k,
    };
    styles[`${key}Name`] = {
      color,
      fontWeight: "bold",
      fontSize: 13 * k,
      fontFamily: "sans-serif",
      lineHeight: 16 * k,
    };
  });
  return styles;
}

const richTextStyles = buildRichTextStyles();
/** Échelle des textes de la roue imprimée (cf. `buildRichTextStyles`). */
const ECHELLE_IMPRESSION = 2;
const richTextStylesImpression = buildRichTextStyles(ECHELLE_IMPRESSION);
const uniqueRegions = Object.keys(REGION_COLORS);

/** Nom de la série des traits — sert aussi de cible au filtrage par légende. */
export const PAMI_SERIE_TRAITS = "Traits de personnalité";

/** Étiquette d'un trait : son nom coloré, suivi du badge portant le score. */
function formatterLabel(nom: string, valeur: number, region: string, couper: boolean) {
  const key = getSafeKey(region);
  return `{${key}Name|${couper ? wrapName(nom) : nom}}  {${key}Badge|${valeur}}`;
}

/**
 * Traits scorés, prêts à être posés dans la série. Exposé à part car le
 * filtrage par la légende (côté navigateur) rejoue un sous-ensemble de ces
 * mêmes items.
 */
export function buildPamiSeriesData(
  scores: Record<string, number>,
  { printMode = false, serializable = false } = {},
): PamiSeriesItem[] {
  return RAW_TRAITS.map((t) => {
    const value = scores[t.key] ?? 5;
    return {
      value,
      name: t.name,
      region: t.region,
      itemStyle: { color: REGION_COLORS[t.region] },
      // En mode sérialisable, l'étiquette est calculée ICI, une fois par trait,
      // et posée en chaîne fixe sur l'item.
      ...(serializable
        ? {
            label: {
              formatter: formatterLabel(t.name, value, t.region, printMode),
            },
          }
        : {}),
    };
  });
}

export interface PamiOptionParams {
  /**
   * Mise en page destinée à une image figée (PDF, rapport) : pas d'animation
   * ni de toolbox, rayon réduit pour laisser la place aux labels sur 2 lignes,
   * et légende `plain` qui passe à la ligne au lieu de scroller — un scroll
   * tronqué dans une image est définitivement tronqué.
   */
  printMode?: boolean;
  /**
   * Option destinée à traverser le réseau en JSON, vers le service de rendu
   * `generate-echarts-image`.
   *
   * `JSON.stringify` supprime silencieusement les fonctions. Une option qui
   * calcule ses étiquettes dans un `formatter: (params) => …` arriverait donc
   * de l'autre côté SANS formatter : ECharts se rabattrait sur le nom brut du
   * trait et les badges de score disparaîtraient du PNG, sans la moindre
   * erreur pour le signaler. En mode sérialisable, chaque étiquette est donc
   * pré-calculée en chaîne fixe sur son item, et le tooltip — qui n'a aucun
   * sens dans une image — est retiré plutôt que d'être envoyé vide.
   */
  serializable?: boolean;
}

export function buildPamiPolarOption(
  scores: Record<string, number>,
  { printMode = false, serializable = false }: PamiOptionParams = {},
): EChartsOption {
  const seriesData = buildPamiSeriesData(scores, { printMode, serializable });

  const legendDummyData = uniqueRegions.map((region) => ({
    name: region,
    value: 0,
    itemStyle: { color: REGION_COLORS[region] },
  }));

  // Imprimée, la roue rétrécit pour laisser la place à des étiquettes deux
  // fois plus grandes.
  const radius = printMode ? ["7%", "44%"] : ["10%", "60%"];
  const k = printMode ? ECHELLE_IMPRESSION : 1;

  return {
    animation: !printMode,
    // Imprimée, la roue a déjà un titre de section et une légende dans le
    // document : le sien ferait doublon.
    title: {
      show: !printMode,
      text: "Profil PAMI — vue d'ensemble",
      left: "center",
      top: 16,
      textStyle: { fontSize: 15, fontFamily: "sans-serif", fontWeight: 600 },
    },
    ...(serializable
      ? {}
      : {
          tooltip: {
            trigger: "item" as const,
            formatter: (params: {
              data?: { region: string };
              value: number;
              name: string;
            }) => {
              if (!params.data || params.value === 0) return "";
              return `<b>${params.name}</b> : ${params.value}/10<br/>Domaine : ${params.data.region}`;
            },
          },
        }),
    legend: {
      bottom: 8 * k,
      type: printMode ? "plain" : "scroll",
      data: uniqueRegions,
      // Imprimée : des pastilles rondes, comme celles des niveaux du rapport.
      ...(printMode ? { icon: "circle" } : {}),
      itemWidth: (printMode ? 14 : 25) * k,
      itemHeight: 14 * k,
      itemGap: 10 * k,
      textStyle: { fontSize: 12 * k, fontFamily: "sans-serif" },
    },
    toolbox: {
      show: !printMode,
      feature: { saveAsImage: { show: true } },
    },
    polar: { radius, center: ["50%", "50%"] },
    angleAxis: { show: false },
    radiusAxis: {
      min: 0,
      max: 10,
      splitNumber: 5,
      z: 10,
      splitLine: {
        show: true,
        lineStyle: { type: "dotted", color: "rgba(0,0,0,0.15)", width: 1 },
      },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
    },
    series: [
      { type: "scatter", coordinateSystem: "polar", data: [] },
      {
        name: PAMI_SERIE_TRAITS,
        type: "pie",
        radius,
        center: ["50%", "50%"],
        roseType: "area",
        itemStyle: { borderRadius: 6, borderColor: "#fff", borderWidth: 1.5 },
        label: {
          show: true,
          // Les styles `rich` restent au niveau de la série : les étiquettes
          // par item, en mode sérialisable, y font référence par leur clé.
          rich: printMode ? richTextStylesImpression : richTextStyles,
          ...(serializable
            ? {}
            : {
                formatter: (params: {
                  data: { region: string };
                  name: string;
                  value: number;
                }) =>
                  formatterLabel(
                    params.name,
                    params.value,
                    params.data.region,
                    printMode,
                  ),
              }),
        },
        labelLine: {
          show: true,
          length: 15 * k,
          length2: 20 * k,
          smooth: true,
          ...(printMode ? { lineStyle: { width: 2 } } : {}),
        },
        data: seriesData,
      },
      {
        name: "Legende",
        type: "pie",
        radius: [0, 0],
        itemStyle: { color: "transparent" },
        label: { show: false },
        labelLine: { show: false },
        data: legendDummyData,
      },
    ],
  } as EChartsOption;
}
